import { type API, type BlockAPI, type BlockTool, type BlockToolConstructorOptions } from "@editorjs/editorjs";
import { ConfigProvider } from "antd";
import pl from "antd/es/locale/pl_PL";
import { createRoot, type Root } from "react-dom/client";
import { Provider } from "react-redux";

import { type MediaLibraryItem } from "@fitness-app/data-models/entities/MediaLibrary";
import { createBunnyEmbedUrl, createBunnyPlayUrl } from "@fitness-app/data-models/external/Bunny";

import { antdTheme } from "~/antdTheme";
import i18n from "~/i18nextConfig";
import { store } from "~/store/initializeStore";
import VideoLibraryPicker from "./VideoLibraryPicker";

const TOOLBOX_ICON =
  '<svg width="17" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="14" height="16" rx="2"/><path d="m16 10 6-3v10l-6-3z"/></svg>';

/**
 * Toolbox entry that turns itself into a Bunny player: the block renders a picker, and choosing a
 * video replaces it with the very same `embed` block a pasted Bunny link produces. Nothing of this
 * tool is ever saved - the content keeps holding plain `embed` blocks, so both clients render it
 * without knowing this tool exists.
 */
class VideoLibraryTool implements BlockTool {
  static get toolbox() {
    return { title: i18n.t("products:productResources.form.video"), icon: TOOLBOX_ICON };
  }

  private readonly api: API;
  private readonly block: BlockAPI;
  private root: Root | null = null;

  constructor({ api, block }: BlockToolConstructorOptions) {
    this.api = api;
    this.block = block;
  }

  render() {
    const wrapper = document.createElement("div");

    // The block holds a search input - without this, Enter and Backspace typed into it reach
    // EditorJS's own shortcuts and split or delete the block instead.
    wrapper.addEventListener("keydown", (event) => event.stopPropagation());

    this.root = createRoot(wrapper);
    this.root.render(
      <ConfigProvider locale={pl} theme={antdTheme}>
        <Provider store={store}>
          <VideoLibraryPicker onSelect={this.replaceWithPlayer} />
        </Provider>
      </ConfigProvider>,
    );

    return wrapper;
  }

  save() {
    return {};
  }

  /** A picker left untouched must not reach the saved content. */
  validate() {
    return false;
  }

  destroy() {
    const root = this.root;
    this.root = null;
    // Unmounting while React is still rendering this tree throws - let the current task finish.
    queueMicrotask(() => root?.unmount());
  }

  private replaceWithPlayer = (video: MediaLibraryItem) => {
    const { videoLibraryId, guid } = video.data;

    this.api.blocks.insert(
      "embed",
      {
        service: "bunny",
        source: createBunnyPlayUrl(videoLibraryId, guid),
        embed: createBunnyEmbedUrl(videoLibraryId, guid),
        width: 580,
        height: 320,
        caption: "",
      },
      undefined,
      this.api.blocks.getBlockIndex(this.block.id),
      true,
      true,
    );
  };
}

export default VideoLibraryTool;
